import PlusIcon from "@rsuite/icons/Plus";
import { useContext, useEffect, useRef, useState, type FC } from "react";
import {
  Avatar,
  AvatarGroup,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  IconButton,
  Input,
  Modal,
  Pagination,
  Popover,
  PopoverProps,
  SelectPicker,
  Table,
  TagPicker,
  Whisper,
} from "rsuite";
import MainLayout from "../components/MainLayout";
import { LoadingContext } from "../contexts/LoadingContext";
import { SearchContext } from "../contexts/SearchContext";
import { Product } from "../models/inventory/product";
import { PaginationResponse } from "../objects/pagination";
import { BsCart3, BsEye, BsFilter, BsTrash, BsTrash3 } from "react-icons/bs";
import { errorToast, successToast } from "../utils/helper-ui";
import {
  createProduct,
  deleteProduct,
  getProducts,
  getProductsPending,
  importProduct,
  updateProduct,
} from "../services/inventory/ProductApi";
import { getPagination, money } from "../utils/helper";
import ProductForm from "../components/features/inventory/ProductForm";
import { Link, useNavigate } from "react-router-dom";
import { ValueType } from "rsuite/esm/Checkbox";
import TagIcon from "@rsuite/icons/Tag";
import { FileModel } from "../models/file/file";
import { DiscountType } from "../models/inventory/discount";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
import { FiDownloadCloud } from "react-icons/fi";
import FileDownload from "@rsuite/icons/FileDownload";
import { PiFileXls } from "react-icons/pi";
import { GiChecklist, GiCheckMark } from "react-icons/gi";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { HiXMark } from "react-icons/hi2";
import Select from "react-select";
import { ProductCategory } from "../models/inventory/product_category";
import { getProductCategories } from "../services/inventory/ProductCategoryApi";

const { Column, HeaderCell, Cell } = Table;
interface ProductPageProps {}

const ProductPage: FC<ProductPageProps> = ({}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [products, setProducts] = useState<Product[]>([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const nav = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [uploading, setUploading] = useState(false);
  const [drawerFilter, setDrawerFilter] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string | null>();
  const [categories , setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategories , setSelectedCategories] = useState<string[]>([]);

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === products.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < products.length) {
    indeterminate = true;
  }

  let productStatus = [
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
  ];
  const handleCheckAll = (
    value: ValueType | undefined,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? products.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: ValueType | undefined,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size, filterStatus, selectedCategories]);

  useEffect(() => {
    getProductCategories({page: 1, size: 20}).then((v) => v.json()).then((v) => setCategories(v.data.items));
  
    return () => {
      
    }
  }, []);
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getProducts({
        page: search != "" ? 1 : page,
        size,
        search,
        order: "name",
        status: filterStatus,
        category_ids: selectedCategories.join(",")
      });

      const resJson = await res.json();
      //   console.log(data);
      if (search != "" || filterStatus !== "" || selectedCategories.length > 0) {
        setProducts([...resJson.data.items]);
      } else {
        setProducts((prev) => [...prev, ...resJson.data.items]);
      }
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderMenu: any = (
    { onClose, left, top, className }: any,
    ref: React.MutableRefObject<HTMLDivElement | null>
  ) => {
    const handleSelect = (eventKey: any) => {
      onClose();
      console.log(eventKey);
    };
    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu onSelect={handleSelect}>
          <Dropdown.Item
            eventKey={1}
            onClick={() => {
              setModalFormOpen(true);
            }}
            icon={<PlusIcon />}
          >
            Create Product
          </Dropdown.Item>
          <Dropdown.Item
            eventKey={2}
            onClick={() => {
              fileRef.current?.click();
            }}
            icon={<PiFileXls />}
          >
            Import From File
          </Dropdown.Item>
          <Dropdown.Item
            eventKey={3}
            onClick={() => {
              window.open("/files/sample-data.xlsx");
            }}
            icon={<FiDownloadCloud />}
          >
            Download Sample
          </Dropdown.Item>
          {checkedKeys.length > 0 && (
            <Dropdown.Item
              eventKey={4}
              onClick={async () => {
                try {
                  setIsLoading(true);
                  for (const productId of checkedKeys) {
                    let prod = products.find(
                      (product) => product.id === productId
                    );
                    if (prod) {
                      await updateProduct(prod.id!, {
                        ...prod,
                        status: "ACTIVE",
                      });
                      setProducts((prev) => [
                        ...prev.map((product) => {
                          if (product.id === productId) {
                            return {
                              ...product,
                              status: "ACTIVE",
                            };
                          }
                          return product;
                        }),
                      ]);
                    }
                  }
                } catch (error) {
                  errorToast(`${error}`);
                } finally {
                  setIsLoading(false);
                }
              }}
              icon={<GiCheckMark />}
            >
              Set Active
            </Dropdown.Item>
          )}
          {checkedKeys.length > 0 && (
            <Dropdown.Item
              eventKey={4}
              onClick={async () => {
                try {
                  setIsLoading(true);
                  for (const productId of checkedKeys) {
                    let prod = products.find(
                      (product) => product.id === productId
                    );
                    if (prod) {
                      await updateProduct(prod.id!, {
                        ...prod,
                        status: "INACTIVE",
                      });

                      setProducts((prev) => [
                        ...prev.map((product) => {
                          if (product.id === productId) {
                            return {
                              ...product,
                              status: "INACTIVE",
                            };
                          }
                          return product;
                        }),
                      ]);
                    }
                  }
                } catch (error) {
                  errorToast(`${error}`);
                } finally {
                  setIsLoading(false);
                }
              }}
              icon={<HiXMark />}
            >
              Set Inactive
            </Dropdown.Item>
          )}
          {checkedKeys.length > 0 && (
            <Dropdown.Item
              eventKey={4}
              onClick={async () => {
                try {
                  if (
                    window.confirm(
                      "Are you sure you want to delete the selected products?"
                    )
                  ) {
                    setIsLoading(true);
                    for (const productId of checkedKeys) {
                      await deleteProduct(productId);

                      setProducts((prev) => [
                        ...prev.filter((product) => product.id !== productId),
                      ]);
                    }
                  }
                } catch (error) {
                  errorToast(`${error}`);
                } finally {
                  setIsLoading(false);
                }
              }}
              icon={<BsTrash />}
            >
              Delete
            </Dropdown.Item>
          )}
          <Dropdown.Item
            eventKey={5}
            onClick={() => {
              setDrawerFilter(true);
            }}
            icon={<BsFilter />}
          >
            Filter
          </Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold"> Product</h1>
      {/* button */}
      <Whisper placement="bottomEnd" trigger="click" speaker={renderMenu}>
        <IconButton appearance="primary" icon={<PlusIcon />} placement="left">
          Action
        </IconButton>
      </Whisper>
      {/* <div className="flex flex-row gap-2">
        <IconButton
          size="sm"
          appearance="primary"
          color="violet"
          icon={<FileDownload />}
          onClick={() => {
            // fileRef.current?.click();
            window.open("/files/sample-data.xlsx");
          }}
          loading={uploading}
        >
          Download Sample
        </IconButton>
       
        <IconButton
          size="sm"
          appearance="primary"
          color="blue"
          icon={<FileDownloadIcon />}
          onClick={() => {
            fileRef.current?.click();
          }}
          loading={uploading}
        >
          Import Excel
        </IconButton>
        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Product
        </IconButton>
      </div> */}
    </div>
  );

  const renderInputFile = () => (
    <input
      type="file"
      accept=".xls,.xlsx"
      ref={fileRef}
      className="hidden"
      onChange={async (val) => {
        if (val.target.files?.length == 0) return;
        try {
          setIsLoading(true);
          setUploading(true);
          // console.log(val.target.files![0]);
          await importProduct(val.target.files![0]);
          successToast(
            "Product import is running in the background, please check product list later to see the changes,"
          );
        } catch (error) {
          errorToast(`${error}`);
        } finally {
          setUploading(false);
          setIsLoading(false);
        }
      }}
    />
  );

  const removeProduct = async (product: Product) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete product with name ${product.name}?`
        )
      ) {
        await deleteProduct(product.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProduct = async (product: Product) => {
    try {
      setIsLoading(true);
      if (selectedProduct) {
        await updateProduct(selectedProduct.id!, product);
      } else {
        await createProduct(product);
      }
      setModalFormOpen(false);
      setSelectedProduct(undefined);
      setProducts([]);
      fetchData();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th
                className="px-4 py-2 text-left border bg-gray-50"
                style={{ width: 100 }}
              >
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </th>
              <th
                className="px-4 py-2 text-left border bg-gray-50"
                style={{ width: "30%" }}
              >
                Name
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Images</th>
              <th className="px-4 py-2 text-left border bg-gray-50">SKU</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Status</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Price</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Brand</th>
              <th className="px-4 py-2 text-left border bg-gray-50">
                Category
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Stock</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, i) => (
              <tr key={i}>
                <td className="px-4 py-2 border">
                  <Checkbox
                    value={product.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((item) => item === product.id)}
                  />
                  {i + 1}
                </td>
                <td className="px-4 py-2 border">
                  <Link
                    to={`/product/${product.id}`}
                    className="hover:underline"
                  >
                    {product.name}
                  </Link>
                </td>
                <td className="px-4 py-2 border">
                  <div className="flex flex-wrap">
                    <AvatarGroup stack>
                      {(product?.product_images ?? [])
                        .filter((p: FileModel, i: number) => i < 5)
                        .map((p: FileModel) => (
                          <Avatar
                            size="sm"
                            bordered
                            circle
                            key={p.id}
                            src={p.url}
                            alt={p.file_name}
                          />
                        ))}
                      {(product?.product_images ?? []).length - 5 > 0 && (
                        <Avatar
                          size="sm"
                          bordered
                          circle
                          style={{ background: "#111" }}
                        >
                          +{(product?.product_images ?? []).length - 5}
                        </Avatar>
                      )}
                    </AvatarGroup>
                  </div>
                </td>
                <td className="px-4 py-2 border">{product.sku}</td>
                <td className="px-4 py-2 border">
                  <Badge
                    color={
                      product.status === "ACTIVE"
                        ? "green"
                        : product.status === "INACTIVE"
                        ? "red"
                        : "cyan"
                    }
                    content={product.status}
                  />
                </td>
                <td className="px-4 py-2 border">{money(product.price)}</td>
                <td className="px-4 py-2 border">{product.brand?.name}</td>
                <td className="px-4 py-2 border">{product.category?.name}</td>
                <td className="px-4 py-2 border">
                  {money(product.total_stock)}
                </td>
                <td className="px-4 py-2 border">
                  <div className="flex flex-row gap-2">
                    <BsEye
                      className="text-green-500 hover:text-green-800 cursor-pointer"
                      size={16}
                      onClick={() => nav(`/product/${product.id}`)}
                    />
                    <BsTrash3
                      className="text-red-500 hover:text-red-800 cursor-pointer"
                      size={16}
                      onClick={() => removeProduct(product)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <small>Total Record : {pagination?.total} items</small>
      {!pagination?.last && (
        <div className="flex justify-center my-2">
          <Button
            onClick={() => {
              setPage((pagination?.page ?? 0) + 1);
            }}
          >
            Load more
          </Button>
        </div>
      )}
      {/* <Table autoHeight data={products} onRowClick={(data, i) => {}}>
        <Column width={50} align="center" fixed>
          <HeaderCell style={{ padding: 0 }}>
            <div style={{ lineHeight: "40px" }}>
              <Checkbox
                inline
                checked={checked}
                indeterminate={indeterminate}
                onChange={handleCheckAll}
              />
            </div>
          </HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(rowData) => (
              <div style={{ lineHeight: "46px" }}>
                <Checkbox
                  value={rowData.id}
                  inline
                  onChange={handleCheck}
                  checked={checkedKeys.some((item) => item === rowData.id)}
                />
              </div>
            )}
          </Cell>
        </Column>
        <Column width={70} align="left" fixed>
          <HeaderCell>No</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>
                {(page - 1) * size + products.indexOf(rowData as Product) + 1}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={200} resizable fixed>
          <HeaderCell>Name</HeaderCell>
          <Cell>
            {(rowData) => (
              <div
                className="hover:underline cursor-pointer font-semibold "
                onClick={() => {
                  nav(`/product/${rowData.id}`);
                }}
              >
                {rowData.name}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={300} resizable>
          <HeaderCell>Images</HeaderCell>
          <Cell>
            {(rowData) => (
              <div className="flex flex-wrap">
                <AvatarGroup stack>
                  {(rowData?.product_images ?? [])
                    .filter((p: FileModel, i: number) => i < 5)
                    .map((p: FileModel) => (
                      <Avatar size="xs"
                        bordered
                        circle
                        key={p.id}
                        src={p.url}
                        alt={p.file_name}
                      />
                    ))}
                  {(rowData?.product_images ?? []).length - 5 > 0 && (
                    <Avatar size="xs" bordered circle style={{ background: "#111" }}>
                      +{(rowData?.product_images ?? []).length - 5}
                    </Avatar>
                  )}
                </AvatarGroup>
              </div>
            )}
          </Cell>
        </Column>
        <Column width={300} resizable>
          <HeaderCell>Description</HeaderCell>
          <Cell dataKey="description" />
        </Column>

        <Column width={150} resizable>
          <HeaderCell>SKU</HeaderCell>
          <Cell dataKey="sku" />
        </Column>

        <Column width={150} resizable>
          <HeaderCell>Barcode</HeaderCell>
          <Cell dataKey="barcode" />
        </Column>

        <Column width={100} resizable>
          <HeaderCell>Price</HeaderCell>
          <Cell>{(rowData) => <div>{money(rowData.price)}</div>}</Cell>
        </Column>
        <Column width={150} resizable>
          <HeaderCell>Brand</HeaderCell>
          <Cell>
            {(rowData) => <div>{rowData.brand ? rowData.brand.name : ""}</div>}
          </Cell>
        </Column>
        <Column width={150} resizable>
          <HeaderCell>Category</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>{rowData.category ? rowData.category.name : ""}</div>
            )}
          </Cell>
        </Column>
        <Column width={100} resizable align="right">
          <HeaderCell>Total Stock</HeaderCell>
          <Cell>{(rowData) => <div>{money(rowData.total_stock)}</div>}</Cell>
        </Column>
        <Column width={100} resizable align="right">
          <HeaderCell>Discount</HeaderCell>
          <Cell>{(rowData) => 
            <div>{rowData.active_discount ? `${money(rowData.active_discount?.value)}${rowData.active_discount?.type == DiscountType.PERCENTAGE ? '%' : '' }` : ''}</div>}
            </Cell>
        </Column>
        <Column width={200} resizable>
          <HeaderCell>Action</HeaderCell>
          <Cell>
            {(rowData) => (
              <div className="flex flex-row gap-2">
                <BsTrash3
                  className="text-red-500 hover:text-red-800 cursor-pointer"
                  size={16}
                  onClick={() => removeProduct(rowData as Product)}
                />
                <BsEye
                  className="text-green-500 hover:text-green-800 cursor-pointer"
                  size={16}
                  onClick={() => nav(`/product/${rowData.id}`)}
                />
              </div>
            )}
          </Cell>
        </Column>
  
      </Table> */}

      {/* <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div> */}
      {modalFormOpen && (
        <ProductForm
          product={selectedProduct}
          open={modalFormOpen}
          onClose={() => {
            setModalFormOpen(false);
            setSelectedProduct(undefined);
          }}
          onSubmit={saveProduct}
        />
      )}
      {renderInputFile()}
      <Modal open={drawerFilter} onClose={() => setDrawerFilter(false)}>
        <Modal.Header>Filter</Modal.Header>
        <Modal.Body>
          <div className="w-full">
            <div className="form-group">
              <label>Status</label>
              <div className="input">
                <SelectPicker
                  block
                  data={productStatus}
                  value={filterStatus}
                  onChange={(v) => {
                    setFilterStatus(v);
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Category</label>
              <div className="input">
                <TagPicker
                  block
                  data={categories}
                  value={selectedCategories}
                  labelKey="name"
                  valueKey="id"
                  onChange={(v) => {
                    setSelectedCategories(v);
                  }}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </MainLayout>
  );
};
export default ProductPage;
